import {HttpClient, HttpHeaders, HttpParams} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {AuthorizationError} from "@app/core/shared/domain/authoritzation.error";
import {InfrastructureError} from "@app/core/shared/domain/infrastructure.error";
import {HandlerParameters, HttpMethod} from "@app/core/shared/infrastructure/base.handler/base-handle.parameters";
import {from, Observable, of} from "rxjs";
import {switchMap, take} from "rxjs/operators";
import {isString} from "@app/core/shared/domain/type.utils";

@Injectable({
    providedIn: 'root',
})
export class BaseHandler {

    constructor(
        private http: HttpClient
    ) { }

    execute(parameters: HandlerParameters): Observable<any | InfrastructureError | AuthorizationError> {
        return this.addHeaders(parameters).pipe(
            take(1),
            switchMap(headerDict => this.createHandler(parameters, headerDict))
        );
    }

    private createHandler(parameters: HandlerParameters, headerDict: Map<string, any>) {
        let header: { [index: string]: any } = {};
        headerDict.forEach((value, key) => {
            header[key] = value;
        });

        let params: { [index: string]: any } = {};
        if (parameters.params) {
            parameters.params.forEach((value, key) => {
                params[key] = value;
            });
        }

        let requestOptions = {
            headers: new HttpHeaders(header),
            params: new HttpParams({ fromObject: params })
        };

        const body: any = parameters.body;

        // Trim all the values
        Object.entries(body).forEach(([key, value]) => {
            if (isString(value)) {
                body[key] = (value as string).trim();
            }
        });

        switch (parameters.method) {
            case HttpMethod.get:
                return this.http.get<Response>(parameters.url, requestOptions);
            case HttpMethod.post:
                return this.http.post<Response>(parameters.url, body, requestOptions);
            case HttpMethod.put:
                return this.http.put<Response>(parameters.url, body, requestOptions);
            case HttpMethod.patch:
                return this.http.patch<Response>(parameters.url, body, requestOptions);
            case HttpMethod.delete:
                return this.http.delete<Response>(parameters.url, requestOptions);
        }
    }

    protected addHeaders(parameters: HandlerParameters): Observable<Map<string, any>> {
        // ---------- Add general headers ----------
        let headerDict = parameters.header;
        headerDict.set('Content-Type', 'application/json');

        if (parameters.authorization !== null) {
            if (parameters.authorization instanceof Promise) {
                return from(parameters.authorization).pipe(
                    switchMap(auth => {
                        if (auth !== null) {
                            headerDict.set('Authorization', auth.getBasicAuthorization());
                        }
                        return of(headerDict);
                    })
                );
            } else {
                headerDict.set('Authorization', parameters.authorization.getBasicAuthorization());
            }
        }

        return of(headerDict);
    }
}
